<template>
  <div class="survey-page">
    <van-nav-bar
      :title="isEditMode ? '问卷修改' : '金融人才问卷'"
      left-text="返回"
      left-arrow
      fixed
      placeholder
      @click-left="goBack"
    />

    <div v-if="pageLoading" class="survey-loading">问卷加载中…</div>
    <div v-else class="survey-body">
      <p class="survey-intro">
        {{
          isEditMode
            ? '修改完成后，提交修改金融人才问卷信息将更新至人才库。'
            : '欢迎加入金融自媒体人才库，请如实填写以下问卷，便于协会为您提供匹配服务。'
        }}
      </p>

      <div class="survey-progress">
        <div class="progress-head">
          <span class="progress-title">填写进度</span>
          <span class="progress-count">{{ surveyProgress.filled }}/{{ surveyProgress.total }}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: surveyProgress.percent + '%' }" />
        </div>
        <p v-if="nextPendingLabels.length" class="progress-pending">
          待完善：{{ nextPendingLabels.join('、') }}
        </p>
      </div>

      <section class="form-section">
        <h3 class="section-title">基本信息</h3>
        <div ref="field_survey_name" class="field-wrap" :class="fieldStateClass('survey_name')">
          <van-field v-model="form.survey_name" label="姓名" placeholder="请输入姓名" maxlength="32" required @input="clearFieldError('survey_name')" />
          <p v-if="fieldErrors.survey_name" class="field-error-tip">{{ fieldErrors.survey_name }}</p>
          <p v-else-if="pendingHints.survey_name" class="field-pending-tip">{{ pendingHints.survey_name }}</p>
        </div>
        <div ref="field_region" class="field-wrap region-block" :class="regionFieldStateClass">
          <div ref="field_region_area">
            <van-field
              readonly
              clickable
              label="所在地区"
              required
              :value="form.region_area_text"
              placeholder="请选择省 / 市 / 区"
              is-link
              @click="showAreaPicker = true"
            />
            <p v-if="fieldErrors.region_area" class="field-error-tip">{{ fieldErrors.region_area }}</p>
            <p v-else-if="pendingHints.region_area" class="field-pending-tip">{{ pendingHints.region_area }}</p>
          </div>
          <div ref="field_region_detail" class="region-detail-wrap" :class="{ 'has-error': !!fieldErrors.region_detail, 'has-pending': !!pendingHints.region_detail && !fieldErrors.region_detail }">
            <van-field
              v-model="form.region_detail"
              label="详细地址"
              placeholder="街道、小区、门牌号等"
              maxlength="128"
              required
              @input="clearFieldError('region_detail')"
            />
            <p v-if="fieldErrors.region_detail" class="field-error-tip">{{ fieldErrors.region_detail }}</p>
            <p v-else-if="pendingHints.region_detail" class="field-pending-tip">{{ pendingHints.region_detail }}</p>
          </div>
        </div>
        <div ref="field_contact_phone" class="field-wrap" :class="[fieldStateClass('contact_phone'), { 'field-readonly': isEditMode }]">
          <van-field
            v-model="form.contact_phone"
            label="手机号"
            type="tel"
            placeholder="11位手机号"
            maxlength="11"
            required
            :readonly="isEditMode"
            @input="clearFieldError('contact_phone')"
          />
          <p v-if="fieldErrors.contact_phone" class="field-error-tip">{{ fieldErrors.contact_phone }}</p>
          <p v-else-if="isEditMode" class="field-readonly-tip">账号绑定手机号，不可修改</p>
          <p v-else-if="pendingHints.contact_phone" class="field-pending-tip">{{ pendingHints.contact_phone }}</p>
        </div>
        <div ref="field_wechat_id" class="field-wrap" :class="fieldStateClass('wechat_id')">
          <van-field v-model="form.wechat_id" label="微信号" placeholder="请输入微信号" maxlength="64" required @input="clearFieldError('wechat_id')" />
          <p v-if="fieldErrors.wechat_id" class="field-error-tip">{{ fieldErrors.wechat_id }}</p>
          <p v-else-if="pendingHints.wechat_id" class="field-pending-tip">{{ pendingHints.wechat_id }}</p>
        </div>
        <div ref="field_douyin_nickname" class="field-wrap" :class="fieldStateClass('douyin_nickname')">
          <van-field v-model="form.douyin_nickname" label="抖音昵称" placeholder="请输入抖音昵称" maxlength="64" required @input="clearFieldError('douyin_nickname')" />
          <p v-if="fieldErrors.douyin_nickname" class="field-error-tip">{{ fieldErrors.douyin_nickname }}</p>
          <p v-else-if="pendingHints.douyin_nickname" class="field-pending-tip">{{ pendingHints.douyin_nickname }}</p>
        </div>
        <div ref="field_douyin_account_id" class="field-wrap" :class="fieldStateClass('douyin_account_id')">
          <van-field
            v-model="form.douyin_account_id"
            label="抖音账号ID"
            placeholder="精确字符，可在抖音个人页复制"
            maxlength="128"
            required
            @input="clearFieldError('douyin_account_id')"
          />
          <p v-if="fieldErrors.douyin_account_id" class="field-error-tip">{{ fieldErrors.douyin_account_id }}</p>
          <p v-else-if="pendingHints.douyin_account_id" class="field-pending-tip">{{ pendingHints.douyin_account_id }}</p>
        </div>
      </section>

      <van-popup v-model="showAreaPicker" position="bottom" round>
        <van-area
          :area-list="areaList"
          :value="regionPickerValue"
          title="选择所在地区"
          @confirm="onAreaConfirm"
          @cancel="showAreaPicker = false"
        />
      </van-popup>

      <section class="form-section">
        <h3 class="section-title">账号与从业</h3>
        <div ref="field_fan_count" class="field-block" :class="fieldStateClass('fan_count')">
          <p class="field-label required">当前粉丝量级</p>
          <van-radio-group v-model="form.fan_count" direction="horizontal" class="option-wrap" @change="clearFieldError('fan_count')">
            <van-radio v-for="opt in fanCountOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.fan_count" class="field-error-tip">{{ fieldErrors.fan_count }}</p>
          <p v-else-if="pendingHints.fan_count" class="field-pending-tip">{{ pendingHints.fan_count }}</p>
        </div>

        <div ref="field_finance_qualifications" class="field-block" :class="fieldBlockStateClass('finance_qualifications', ['finance_qualification_other'])">
          <p class="field-label required">金融资质（可多选）</p>
          <van-checkbox-group v-model="form.finance_qualifications" direction="horizontal" class="option-wrap" @change="clearFieldError('finance_qualifications')">
            <van-checkbox v-for="opt in financeQualificationOptions" :key="opt" :name="opt" shape="square">
              {{ opt }}
            </van-checkbox>
          </van-checkbox-group>
          <div
            v-if="form.finance_qualifications.includes('其他')"
            ref="field_finance_qualification_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.finance_qualification_other }"
          >
            <van-field
              v-model="form.finance_qualification_other"
              placeholder="请填写其他金融资质"
              maxlength="128"
              @input="clearFieldError('finance_qualification_other')"
            />
          </div>
          <p v-if="fieldErrors.finance_qualifications" class="field-error-tip">{{ fieldErrors.finance_qualifications }}</p>
          <p v-else-if="fieldErrors.finance_qualification_other" class="field-error-tip">{{ fieldErrors.finance_qualification_other }}</p>
          <p v-else-if="pendingHints.finance_qualifications" class="field-pending-tip">{{ pendingHints.finance_qualifications }}</p>
          <p v-else-if="pendingHints.finance_qualification_other" class="field-pending-tip">{{ pendingHints.finance_qualification_other }}</p>
        </div>

        <div ref="field_institution" class="field-block" :class="fieldBlockStateClass('institution', ['institution_other'])">
          <p class="field-label required">目前所属机构</p>
          <van-radio-group v-model="form.institution" @change="clearFieldError('institution')">
            <van-radio v-for="opt in institutionOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <div
            v-if="form.institution === '其他'"
            ref="field_institution_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.institution_other }"
          >
            <van-field
              v-model="form.institution_other"
              placeholder="请填写机构名称"
              maxlength="128"
              @input="clearFieldError('institution_other')"
            />
          </div>
          <p v-if="fieldErrors.institution" class="field-error-tip">{{ fieldErrors.institution }}</p>
          <p v-else-if="fieldErrors.institution_other" class="field-error-tip">{{ fieldErrors.institution_other }}</p>
          <p v-else-if="pendingHints.institution" class="field-pending-tip">{{ pendingHints.institution }}</p>
          <p v-else-if="pendingHints.institution_other" class="field-pending-tip">{{ pendingHints.institution_other }}</p>
        </div>

        <div ref="field_work_years" class="field-block" :class="fieldStateClass('work_years')">
          <p class="field-label required">金融从业年限</p>
          <van-radio-group v-model="form.work_years" direction="horizontal" class="option-wrap" @change="clearFieldError('work_years')">
            <van-radio v-for="opt in workYearsOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.work_years" class="field-error-tip">{{ fieldErrors.work_years }}</p>
          <p v-else-if="pendingHints.work_years" class="field-pending-tip">{{ pendingHints.work_years }}</p>
        </div>

        <div ref="field_violation_history" class="field-block" :class="fieldBlockStateClass('violation_history', ['violation_history_other'])">
          <p class="field-label required">账号历史违规</p>
          <van-radio-group v-model="form.violation_history" @change="clearFieldError('violation_history')">
            <van-radio v-for="opt in violationOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <div
            v-if="form.violation_history === '其他'"
            ref="field_violation_history_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.violation_history_other }"
          >
            <van-field
              v-model="form.violation_history_other"
              placeholder="请说明违规情况"
              maxlength="256"
              @input="clearFieldError('violation_history_other')"
            />
          </div>
          <p v-if="fieldErrors.violation_history" class="field-error-tip">{{ fieldErrors.violation_history }}</p>
          <p v-else-if="fieldErrors.violation_history_other" class="field-error-tip">{{ fieldErrors.violation_history_other }}</p>
          <p v-else-if="pendingHints.violation_history" class="field-pending-tip">{{ pendingHints.violation_history }}</p>
          <p v-else-if="pendingHints.violation_history_other" class="field-pending-tip">{{ pendingHints.violation_history_other }}</p>
        </div>
      </section>

      <section class="form-section">
        <h3 class="section-title">直播与内容</h3>
        <div ref="field_live_platforms" class="field-block" :class="fieldBlockStateClass('live_platforms', ['live_platform_other'])">
          <p class="field-label required">主要直播平台（可多选）</p>
          <van-checkbox-group v-model="form.live_platforms" direction="horizontal" class="option-wrap" @change="clearFieldError('live_platforms')">
            <van-checkbox v-for="opt in livePlatformOptions" :key="opt" :name="opt" shape="square">
              {{ opt }}
            </van-checkbox>
          </van-checkbox-group>
          <div
            v-if="form.live_platforms.includes('其他')"
            ref="field_live_platform_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.live_platform_other }"
          >
            <van-field
              v-model="form.live_platform_other"
              placeholder="请填写其他平台"
              maxlength="128"
              @input="clearFieldError('live_platform_other')"
            />
          </div>
          <p v-if="fieldErrors.live_platforms" class="field-error-tip">{{ fieldErrors.live_platforms }}</p>
          <p v-else-if="fieldErrors.live_platform_other" class="field-error-tip">{{ fieldErrors.live_platform_other }}</p>
          <p v-else-if="pendingHints.live_platforms" class="field-pending-tip">{{ pendingHints.live_platforms }}</p>
          <p v-else-if="pendingHints.live_platform_other" class="field-pending-tip">{{ pendingHints.live_platform_other }}</p>
        </div>

        <div ref="field_live_experience" class="field-block" :class="fieldStateClass('live_experience')">
          <p class="field-label required">直播经验</p>
          <van-radio-group v-model="form.live_experience" @change="clearFieldError('live_experience')">
            <van-radio v-for="opt in liveExperienceOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.live_experience" class="field-error-tip">{{ fieldErrors.live_experience }}</p>
          <p v-else-if="pendingHints.live_experience" class="field-pending-tip">{{ pendingHints.live_experience }}</p>
        </div>

        <div ref="field_content_tracks" class="field-block" :class="fieldBlockStateClass('content_tracks', ['content_track_other'])">
          <p class="field-label required">核心内容赛道（可多选）</p>
          <van-checkbox-group v-model="form.content_tracks" @change="clearFieldError('content_tracks')">
            <van-checkbox v-for="opt in contentTrackOptions" :key="opt" :name="opt" shape="square">
              {{ opt }}
            </van-checkbox>
          </van-checkbox-group>
          <div
            v-if="form.content_tracks.includes('其他')"
            ref="field_content_track_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.content_track_other }"
          >
            <van-field
              v-model="form.content_track_other"
              placeholder="请填写其他内容赛道"
              maxlength="128"
              @input="clearFieldError('content_track_other')"
            />
          </div>
          <p v-if="fieldErrors.content_tracks" class="field-error-tip">{{ fieldErrors.content_tracks }}</p>
          <p v-else-if="fieldErrors.content_track_other" class="field-error-tip">{{ fieldErrors.content_track_other }}</p>
          <p v-else-if="pendingHints.content_tracks" class="field-pending-tip">{{ pendingHints.content_tracks }}</p>
          <p v-else-if="pendingHints.content_track_other" class="field-pending-tip">{{ pendingHints.content_track_other }}</p>
        </div>
      </section>

      <section class="form-section">
        <h3 class="section-title">期望与态度</h3>
        <div ref="field_career_motivation" class="field-block" :class="fieldStateClass('career_motivation')">
          <p class="field-label required">从业动机</p>
          <van-radio-group v-model="form.career_motivation" direction="horizontal" class="option-wrap" @change="clearFieldError('career_motivation')">
            <van-radio v-for="opt in careerMotivationOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.career_motivation" class="field-error-tip">{{ fieldErrors.career_motivation }}</p>
          <p v-else-if="pendingHints.career_motivation" class="field-pending-tip">{{ pendingHints.career_motivation }}</p>
        </div>

        <div ref="field_association_expectation" class="field-block" :class="fieldBlockStateClass('association_expectation', ['association_expectation_other'])">
          <p class="field-label required">期望协会在金融人才职业发展上</p>
          <van-radio-group v-model="form.association_expectation" @change="clearFieldError('association_expectation')">
            <van-radio v-for="opt in associationExpectationOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <div
            v-if="form.association_expectation === '其他'"
            ref="field_association_expectation_other"
            class="other-input-wrap"
            :class="{ 'has-error': !!fieldErrors.association_expectation_other }"
          >
            <van-field
              v-model="form.association_expectation_other"
              placeholder="请填写其他期望"
              maxlength="256"
              @input="clearFieldError('association_expectation_other')"
            />
          </div>
          <p v-if="fieldErrors.association_expectation" class="field-error-tip">{{ fieldErrors.association_expectation }}</p>
          <p v-else-if="fieldErrors.association_expectation_other" class="field-error-tip">{{ fieldErrors.association_expectation_other }}</p>
          <p v-else-if="pendingHints.association_expectation" class="field-pending-tip">{{ pendingHints.association_expectation }}</p>
          <p v-else-if="pendingHints.association_expectation_other" class="field-pending-tip">{{ pendingHints.association_expectation_other }}</p>
        </div>

        <div ref="field_location_preference" class="field-block" :class="fieldStateClass('location_preference')">
          <p class="field-label required">金融岗位地域偏好</p>
          <van-radio-group v-model="form.location_preference" @change="clearFieldError('location_preference')">
            <van-radio v-for="opt in locationPreferenceOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.location_preference" class="field-error-tip">{{ fieldErrors.location_preference }}</p>
          <p v-else-if="pendingHints.location_preference" class="field-pending-tip">{{ pendingHints.location_preference }}</p>
        </div>

        <div ref="field_honor_expectation" class="field-block" :class="fieldStateClass('honor_expectation')">
          <p class="field-label required">除职业发展扶持外，还希望协会</p>
          <van-radio-group v-model="form.honor_expectation" @change="clearFieldError('honor_expectation')">
            <van-radio v-for="opt in honorExpectationOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.honor_expectation" class="field-error-tip">{{ fieldErrors.honor_expectation }}</p>
          <p v-else-if="pendingHints.honor_expectation" class="field-pending-tip">{{ pendingHints.honor_expectation }}</p>
        </div>

        <div ref="field_research_activity" class="field-block" :class="fieldStateClass('research_activity')">
          <p class="field-label required">对上市公司调研、企业走访活动</p>
          <van-radio-group v-model="form.research_activity" direction="horizontal" @change="clearFieldError('research_activity')">
            <van-radio v-for="opt in researchActivityOptions" :key="opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.research_activity" class="field-error-tip">{{ fieldErrors.research_activity }}</p>
          <p v-else-if="pendingHints.research_activity" class="field-pending-tip">{{ pendingHints.research_activity }}</p>
        </div>

        <div ref="field_social_voice" class="field-block" :class="fieldStateClass('social_voice')">
          <p class="field-label required">是否愿意为社会发声，宣导积极正能量</p>
          <van-radio-group v-model="form.social_voice" direction="horizontal" @change="clearFieldError('social_voice')">
            <van-radio v-for="opt in yesNoOptions" :key="'social-' + opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.social_voice" class="field-error-tip">{{ fieldErrors.social_voice }}</p>
          <p v-else-if="pendingHints.social_voice" class="field-pending-tip">{{ pendingHints.social_voice }}</p>
        </div>

        <div ref="field_compliance_commitment" class="field-block" :class="fieldStateClass('compliance_commitment')">
          <p class="field-label required">是否愿意遵守金融行业合规性和从业廉洁</p>
          <van-radio-group v-model="form.compliance_commitment" direction="horizontal" @change="clearFieldError('compliance_commitment')">
            <van-radio v-for="opt in yesNoOptions" :key="'compliance-' + opt" :name="opt">{{ opt }}</van-radio>
          </van-radio-group>
          <p v-if="fieldErrors.compliance_commitment" class="field-error-tip">{{ fieldErrors.compliance_commitment }}</p>
          <p v-else-if="pendingHints.compliance_commitment" class="field-pending-tip">{{ pendingHints.compliance_commitment }}</p>
        </div>

        <div ref="field_extra_support" class="field-wrap" :class="fieldStateClass('extra_support')">
          <van-field
            v-model="form.extra_support"
            rows="3"
            autosize
            type="textarea"
            label="其他期望"
            placeholder="综上以外，还期望自媒V发布提供哪些帮助和支持"
            maxlength="500"
            show-word-limit
            required
            @input="clearFieldError('extra_support')"
          />
          <p v-if="fieldErrors.extra_support" class="field-error-tip">{{ fieldErrors.extra_support }}</p>
          <p v-else-if="pendingHints.extra_support" class="field-pending-tip">{{ pendingHints.extra_support }}</p>
        </div>
      </section>

      <section v-if="!isEditMode && registerDraft" class="verify-section">
        <h3 class="section-title">手机验证</h3>
        <p class="verify-tip">
          问卷填写完成后，方可向 {{ maskedRegisterPhone }} 发送验证码
        </p>
        <div ref="field_verify_code" class="verify-code-row" :class="{ 'has-error': !!fieldErrors.verify_code }">
          <input
            v-model="verifyCode"
            type="tel"
            class="verify-code-input"
            placeholder="请输入验证码"
            maxlength="6"
            @input="clearFieldError('verify_code')"
          />
          <button
            type="button"
            class="verify-code-btn"
            :disabled="!canGetVerifyCode || isCounting || getCodeLoading"
            @click="handleGetCode"
          >
            {{
              getCodeLoading
                ? '发送中…'
                : isCounting
                  ? `${countdown}s后重发`
                  : '获取验证码'
            }}
          </button>
        </div>
        <p v-if="fieldErrors.verify_code" class="field-error-tip verify-error">{{ fieldErrors.verify_code }}</p>
        <p v-else-if="!canGetVerifyCode" class="verify-pending-tip">请先完善上方问卷后再获取验证码</p>
      </section>

      <button class="submit-btn" :disabled="submitting" @click="handleSubmit">
        {{
          submitting
            ? (isEditMode ? '提交修改中…' : '提交注册中…')
            : (isEditMode ? '提交修改' : '提交注册')
        }}
      </button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { areaList } from '@vant/area-data'
import { scrollToFormField } from '@/utils/formScroll'
import {
  emptyFinanceSurvey,
  validateFinanceSurvey,
  normalizeFinanceSurvey,
  snapshotFinanceSurveyForm,
  applyFinanceSurveyPayloadToForm,
  getFinanceSurveyPendingHints,
  getFinanceSurveyProgress,
  formatRegionAreaText,
  FINANCE_SURVEY_LABELS,
  FAN_COUNT_OPTIONS,
  FINANCE_QUALIFICATION_OPTIONS,
  INSTITUTION_OPTIONS,
  WORK_YEARS_OPTIONS,
  VIOLATION_OPTIONS,
  LIVE_PLATFORM_OPTIONS,
  LIVE_EXPERIENCE_OPTIONS,
  CONTENT_TRACK_OPTIONS,
  CAREER_MOTIVATION_OPTIONS,
  ASSOCIATION_EXPECTATION_OPTIONS,
  LOCATION_PREFERENCE_OPTIONS,
  HONOR_EXPECTATION_OPTIONS,
  RESEARCH_ACTIVITY_OPTIONS,
  YES_NO_OPTIONS,
} from '@/constants/financeSurvey'
import { saveLoginSession } from '@/utils/loginSession'

const REGISTER_DRAFT_KEY = 'h5_register_draft'

function loginApiBase() {
  return (process.env.VUE_APP_API_BASE || '').trim().replace(/\/$/, '')
}

function loginPhoneUrl() {
  const base = loginApiBase()
  return base ? `${base}/api/login_phone` : '/api/login_phone'
}

function registerApiBase() {
  return (process.env.VUE_APP_API_BASE || '').trim().replace(/\/$/, '')
}

function registerUserUrl() {
  const base = registerApiBase()
  return base ? `${base}/api/register_user` : '/api/register_user'
}

function financeSurveyGetUrl() {
  const base = registerApiBase()
  return base ? `${base}/api/get_finance_survey` : '/api/get_finance_survey'
}

function financeSurveyUpdateUrl() {
  const base = registerApiBase()
  return base ? `${base}/api/update_finance_survey` : '/api/update_finance_survey'
}

export default {
  name: 'FinanceTalentSurvey',
  data() {
    return {
      form: emptyFinanceSurvey(),
      registerDraft: null,
      editUserId: '',
      editLockedPhone: '',
      pageLoading: false,
      verifyCode: '',
      submitting: false,
      getCodeLoading: false,
      countdown: 60,
      isCounting: false,
      countdownTimer: null,
      fieldErrors: {},
      showAreaPicker: false,
      areaList,
      fanCountOptions: FAN_COUNT_OPTIONS,
      financeQualificationOptions: FINANCE_QUALIFICATION_OPTIONS,
      institutionOptions: INSTITUTION_OPTIONS,
      workYearsOptions: WORK_YEARS_OPTIONS,
      violationOptions: VIOLATION_OPTIONS,
      livePlatformOptions: LIVE_PLATFORM_OPTIONS,
      liveExperienceOptions: LIVE_EXPERIENCE_OPTIONS,
      contentTrackOptions: CONTENT_TRACK_OPTIONS,
      careerMotivationOptions: CAREER_MOTIVATION_OPTIONS,
      associationExpectationOptions: ASSOCIATION_EXPECTATION_OPTIONS,
      locationPreferenceOptions: LOCATION_PREFERENCE_OPTIONS,
      honorExpectationOptions: HONOR_EXPECTATION_OPTIONS,
      researchActivityOptions: RESEARCH_ACTIVITY_OPTIONS,
      yesNoOptions: YES_NO_OPTIONS,
    }
  },
  computed: {
    isEditMode() {
      return this.$route.name === 'surveyEdit'
    },
    pendingHints() {
      return getFinanceSurveyPendingHints(this.form)
    },
    surveyProgress() {
      return getFinanceSurveyProgress(this.form)
    },
    nextPendingLabels() {
      return this.surveyProgress.pending.slice(0, 5).map((field) => {
        if (field === 'region_area') return '所在地区'
        if (field === 'region_detail') return '详细地址'
        return FINANCE_SURVEY_LABELS[field] || field
      })
    },
    regionPickerValue() {
      const codes = this.form.region_codes
      return Array.isArray(codes) && codes.length === 3 ? codes[2] : ''
    },
    regionFieldStateClass() {
      const hasError = !!(this.fieldErrors.region_area || this.fieldErrors.region_detail)
      const hasPending = !!(this.pendingHints.region_area || this.pendingHints.region_detail)
      return {
        'has-error': hasError,
        'has-pending': hasPending && !hasError,
      }
    },
    registerPhone() {
      return ((this.registerDraft && this.registerDraft.phone_number) || '').trim()
    },
    maskedRegisterPhone() {
      const phone = this.registerPhone
      if (phone.length !== 11) return phone || '注册手机号'
      return `${phone.slice(0, 3)}****${phone.slice(7)}`
    },
    isRegisterPhoneValid() {
      return /^1[3-9]\d{9}$/.test(this.registerPhone)
    },
    isSurveyComplete() {
      return !validateFinanceSurvey(this.form)
    },
    canGetVerifyCode() {
      return this.isSurveyComplete && this.isRegisterPhoneValid
    },
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  },
  created() {
    if (this.isEditMode) {
      this.loadEditSurvey()
    } else {
      this.loadDraft()
    }
  },
  methods: {
    loadDraft() {
      try {
        const raw = sessionStorage.getItem(REGISTER_DRAFT_KEY)
        if (!raw) {
          Toast.fail('请先填写注册基本信息')
          this.$router.replace({ name: 'login' })
          return
        }
        const draft = JSON.parse(raw)
        if (!draft || !draft.phone_number || !draft.user_name) {
          Toast.fail('注册信息不完整，请重新填写')
          this.$router.replace({ name: 'login' })
          return
        }
        this.registerDraft = draft
        if (draft.verify_code) {
          this.verifyCode = draft.verify_code
        }
        if (draft.user_name && !this.form.survey_name) {
          this.form.survey_name = draft.user_name
        }
        if (draft.phone_number && !this.form.contact_phone) {
          this.form.contact_phone = draft.phone_number
        }
      } catch (_) {
        Toast.fail('注册信息已失效，请重新填写')
        this.$router.replace({ name: 'login' })
      }
    },
    async loadEditSurvey() {
      let uid = ''
      try {
        const raw = sessionStorage.getItem('loginUser')
        if (raw) {
          const u = JSON.parse(raw)
          uid = (u.user_id || '').trim()
        }
      } catch (_) {
        /* ignore */
      }
      if (!uid) {
        Toast.fail('请先登录')
        this.$router.replace({ name: 'login' })
        return
      }
      this.editUserId = uid
      this.pageLoading = true
      try {
        const data = await this.postJson(financeSurveyGetUrl(), { user_id: uid })
        if (!data.success) {
          Toast.fail(data.message || '加载问卷失败')
          this.$router.replace({ name: 'user' })
          return
        }
        this.form = applyFinanceSurveyPayloadToForm(data.finance_survey_form || data)
        const locked = String(data.account_phone || '').replace(/\D/g, '')
        if (locked.length === 11) {
          this.editLockedPhone = locked
          this.form.contact_phone = locked
        }
      } catch (e) {
        Toast.fail((e && e.message) || '加载问卷失败')
        this.$router.replace({ name: 'user' })
      } finally {
        this.pageLoading = false
      }
    },
    goBack() {
      if (this.isEditMode) {
        this.$router.replace({ name: 'user' })
      } else {
        this.$router.replace({ name: 'login' })
      }
    },
    clearFieldError(field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
    },
    fieldStateClass(field) {
      const hasError = !!this.fieldErrors[field]
      const hasPending = !!this.pendingHints[field]
      return {
        'has-error': hasError,
        'has-pending': hasPending && !hasError,
      }
    },
    fieldBlockStateClass(field, extraFields = []) {
      const fields = [field].concat(extraFields)
      const hasError = fields.some((key) => !!this.fieldErrors[key])
      const hasPending = fields.some((key) => !!this.pendingHints[key])
      return {
        'has-error': hasError,
        'has-pending': hasPending && !hasError,
      }
    },
    onAreaConfirm(values) {
      const selected = Array.isArray(values) ? values : []
      this.form.region_codes = selected.map((item) => item.code)
      this.form.region_area_text = formatRegionAreaText(selected[0], selected[1], selected[2])
      this.showAreaPicker = false
      this.clearFieldError('region_area')
    },
    focusFieldError(field, message) {
      this.$set(this.fieldErrors, field, message)
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          scrollToFormField(this, field, { navOffset: 56 })
        })
      })
    },
    async postJson(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const text = await res.text()
      let data = null
      if (text) {
        data = JSON.parse(text)
      } else {
        data = {}
      }
      if (!res.ok) {
        const msg = data.message || `请求失败（${res.status}）`
        throw new Error(msg)
      }
      return data
    },
    _normalizeApiMessage(msg) {
      const t = String(msg || '').trim().toLowerCase()
      if (t.includes('check frequency failed')) {
        return '频繁申请验证码，请稍后重试'
      }
      return String(msg || '').trim()
    },
    startCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
      }
      this.countdown = 60
      this.isCounting = true
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          this.isCounting = false
          this.countdown = 60
        }
      }, 1000)
    },
    async handleGetCode() {
      const surveyInvalid = validateFinanceSurvey(this.form)
      if (surveyInvalid) {
        this.focusFieldError(surveyInvalid.field, surveyInvalid.message)
        Toast.fail('请先完善问卷后再获取验证码')
        return
      }
      if (!this.isRegisterPhoneValid) {
        Toast.fail('注册手机号无效，请返回重新填写')
        return
      }
      if (this.getCodeLoading || this.isCounting) return

      this.getCodeLoading = true
      try {
        const data = await this.postJson(loginPhoneUrl(), {
          task_id: 0,
          phone_number: this.registerPhone,
          biz_type: 'register',
          is_register: true,
        })
        if (data.success) {
          Toast.success(data.message || '验证码已发送')
          this.startCountdown()
        } else {
          Toast.fail(this._normalizeApiMessage(data.message) || '发送失败')
        }
      } catch (e) {
        Toast.fail((e && e.message) || '网络异常，请稍后重试')
      } finally {
        this.getCodeLoading = false
      }
    },
    async handleSubmit() {
      const invalid = validateFinanceSurvey(this.form)
      if (invalid) {
        this.focusFieldError(invalid.field, invalid.message)
        return
      }
      if (this.isEditMode) {
        await this.handleEditSubmit()
        return
      }
      const code = (this.verifyCode || '').trim()
      if (!code) {
        this.focusFieldError('verify_code', '请输入验证码')
        return
      }
      if (code.length < 4 || code.length > 6) {
        this.focusFieldError('verify_code', '请输入正确的验证码（4-6位）')
        return
      }
      if (!this.registerDraft || this.submitting) return

      this.submitting = true
      try {
        const financeSurvey = normalizeFinanceSurvey(this.form)
        const financeSurveyErr = snapshotFinanceSurveyForm(this.form)
        const data = await this.postJson(registerUserUrl(), {
          user_name: this.registerDraft.user_name,
          phone_number: this.registerDraft.phone_number,
          verify_code: code,
          finance_survey: financeSurvey,
          finance_survey_err: financeSurveyErr,
        })
        if (data.success) {
          sessionStorage.removeItem(REGISTER_DRAFT_KEY)
          if (data.user_info) {
            const saved = saveLoginSession(
              { ...data.user_info, first_login: true },
              true
            )
            if (!saved.ok) {
              Toast.fail(saved.message || '登录状态保存失败')
              return
            }
          }
          this.$router.replace({ name: 'user' })
          Toast.success(data.message || '注册成功')
        } else {
          const msg = data.message || '注册失败'
          const invalid = validateFinanceSurvey(this.form)
          if (invalid) {
            this.focusFieldError(invalid.field, invalid.message)
          } else {
            Toast.fail(msg)
          }
        }
      } catch (e) {
        Toast.fail((e && e.message) || '网络异常，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    async handleEditSubmit() {
      if (!this.editUserId || this.submitting) return
      if (this.editLockedPhone) {
        this.form.contact_phone = this.editLockedPhone
      }
      this.submitting = true
      try {
        const financeSurvey = normalizeFinanceSurvey(this.form)
        const financeSurveyErr = snapshotFinanceSurveyForm(this.form)
        const data = await this.postJson(financeSurveyUpdateUrl(), {
          user_id: this.editUserId,
          finance_survey: financeSurvey,
          finance_survey_err: financeSurveyErr,
        })
        if (data.success) {
          this.$router.replace({ name: 'user' })
          Toast.success(data.message || '问卷已更新')
        } else {
          const invalid = validateFinanceSurvey(this.form)
          if (invalid) {
            this.focusFieldError(invalid.field, invalid.message)
          } else {
            Toast.fail(data.message || '修改失败')
          }
        }
      } catch (e) {
        Toast.fail((e && e.message) || '网络异常，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.survey-page {
  min-height: 100vh;
  background: #f5f6fa;
}

.survey-loading {
  padding: 48px 16px;
  text-align: center;
  color: #666;
  font-size: 15px;
}

.survey-body {
  padding: 12px 12px 32px;
}

.survey-intro {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6c5ce7, #4b3f99);
  color: #fff;
  font-size: 14px;
  line-height: 1.6;
}

.survey-progress {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.progress-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.progress-count {
  font-size: 13px;
  color: #6c5ce7;
  font-weight: 600;
}

.progress-bar {
  height: 6px;
  border-radius: 3px;
  background: #ececf3;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, #6c5ce7, #4b3f99);
  transition: width 0.25s ease;
}

.progress-pending {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #ff8f1f;
}

.region-block >>> .van-cell::after {
  border-bottom: 1px solid #f0f0f5;
}

.region-detail-wrap {
  border-top: 1px solid #f0f0f5;
}

.form-section {
  margin-bottom: 14px;
  padding: 12px 0 4px;
  border-radius: 12px;
  background: #fff;
  overflow: visible;
}

.section-title {
  margin: 0 0 8px;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.field-wrap,
.field-block {
  border-top: 1px solid #f0f0f5;
}

.field-block {
  padding: 8px 16px 12px;
}

.field-label {
  margin: 0 0 10px;
  font-size: 14px;
  color: #333;
}

.field-label.required::before {
  content: '*';
  color: #ee0a24;
  margin-right: 4px;
}

.option-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.option-wrap >>> .van-radio,
.option-wrap >>> .van-checkbox {
  margin-right: 0;
  margin-bottom: 4px;
}

.field-wrap.has-error >>> .van-field__control,
.field-block.has-error >>> .van-field__control {
  color: #ee0a24;
}

.field-wrap.has-pending >>> .van-field__control:not(:focus),
.field-block.has-pending >>> .van-field__control:not(:focus) {
  color: #c8c9cc;
}

.field-wrap.has-pending,
.field-block.has-pending {
  background: #fffbf5;
  box-shadow: inset 0 0 0 1px #ffe7ba;
}

.field-block.has-pending {
  border-radius: 8px;
  margin: 0 8px 8px;
  padding: 8px 12px 12px;
}

.field-pending-tip {
  margin: 0;
  padding: 0 16px 8px;
  color: #ff8f1f;
  font-size: 12px;
  line-height: 1.4;
}

.field-block .field-pending-tip {
  padding: 6px 0 0;
}

.field-readonly >>> .van-field__control {
  color: #969799;
}

.field-readonly-tip {
  margin: 0;
  padding: 0 16px 8px;
  color: #969799;
  font-size: 12px;
  line-height: 1.4;
}

.region-detail-wrap.has-pending >>> .van-cell {
  background: transparent;
}

.field-wrap.has-error >>> .van-cell,
.field-block.has-error {
  animation: field-shake 0.45s ease;
}

.field-block.has-error {
  background: #fff5f7;
  box-shadow: inset 0 0 0 1px #ff6b81;
  border-radius: 8px;
  margin: 0 8px 8px;
  padding: 8px 12px 12px;
}

.other-input-wrap.has-error >>> .van-cell {
  background: #fff5f7;
}

.other-input-wrap.has-error {
  animation: field-shake 0.45s ease;
}

.field-error-tip {
  margin: 0;
  padding: 0 16px 8px;
  color: #ee0a24;
  font-size: 12px;
  line-height: 1.4;
  animation: field-tip-in 0.28s ease;
}

.field-block .field-error-tip {
  padding: 6px 0 0;
}

@keyframes field-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

@keyframes field-tip-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.submit-btn {
  display: block;
  width: 100%;
  height: 48px;
  margin-top: 8px;
  border: none;
  border-radius: 24px;
  background: linear-gradient(90deg, #6c5ce7, #4b3f99);
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}

.verify-section {
  margin-bottom: 14px;
  padding: 12px 16px 16px;
  border-radius: 12px;
  background: #fff;
}

.verify-tip {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #666;
}

.verify-code-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.verify-code-row.has-error .verify-code-input {
  box-shadow: 0 0 0 1px #ee0a24 inset;
  background: #fff5f7;
}

.verify-code-input {
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #f5f6fa;
  font-size: 16px;
  outline: none;
}

.verify-code-btn {
  flex-shrink: 0;
  height: 44px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #6c5ce7;
  color: #fff;
  font-size: 14px;
}

.verify-code-btn:disabled {
  background: #b8b0e8;
  cursor: not-allowed;
}

.verify-error,
.verify-pending-tip {
  margin: 8px 0 0;
  padding: 0;
  font-size: 12px;
  line-height: 1.4;
}

.verify-pending-tip {
  color: #ff8f1f;
}

.verify-error {
  color: #ee0a24;
}

.submit-btn:disabled {
  opacity: 0.65;
}
</style>
